import type { TranscriptFragment } from "@repo/app-types/client/client";
import type { Contact } from "@repo/app-types/entities/contact";
import type { Interview } from "@repo/app-types/entities/interview";
import {
  buildInterviewContactDisplayNames,
  getShareLinkForInterview,
  splitSummaryTextIntoLines,
} from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { Link } from "@repo/ui/Link/index";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { type FC, useState } from "react";
import { useNavigate } from "react-router";
import { useCompanyDomainsQuery } from "../../../api/entities/company.js";
import { appPaths } from "../../../app/appPaths.js";
import { copyRichText } from "../../../helpers/utilities.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectProjectById } from "../../projects/projectsSlice.js";

interface ShareInterviewModalContentProps {
  onClose: () => void;
  contact: Contact;
  interview: Interview;
  transcriptFragment: TranscriptFragment | null;
}

/**
 * Contents of the share-interview modal. Expects to be rendered inside a
 * `Modal` that mounts it only while open, so the domains query and the
 * "include details" choice reset per open.
 */
export const ShareInterviewModalContent: FC<
  ShareInterviewModalContentProps
> = ({ onClose, interview, transcriptFragment, contact }) => {
  const navigate = useNavigate();
  // Whether we should include the interview summary or transcript fragment quote
  const [shouldShareDetails, setShouldShareDetails] = useState(true);

  const project = useAppSelector((state) =>
    selectProjectById(state, interview.project_id),
  );

  // The company domains back the guidance on how to share the interview.
  const { data: companyDomains = [] } = useCompanyDomainsQuery();

  // Should not happen
  if (!project) return null;

  // Create the share link
  const { primaryDisplayName } = buildInterviewContactDisplayNames(contact);
  const link = getShareLinkForInterview({
    baseUrl: window.location.origin,
    projectId: project.id,
    interviewId: interview.id,
    primaryDisplayName,
    transcriptFragmentId: transcriptFragment
      ? transcriptFragment.id
      : undefined,
  });

  // Create the share text snippet
  const shareSnippet = generateShareSnippet(
    primaryDisplayName,
    interview,
    transcriptFragment,
    link,
    shouldShareDetails,
  );

  const renderDomainLoginGuidance = () => {
    if (companyDomains.length === 0) {
      return (
        <span>
          {" "}
          Add{" "}
          <span
            className="cursor-pointer font-semibold"
            onClick={() => {
              navigate(appPaths.settingsTeammates.build());
            }}
          >
            approved signup domains
          </span>{" "}
          to allow teammates to sign in with their work email to view the full
          interview transcript.
        </span>
      );
    }

    const domainList =
      companyDomains.length > 1
        ? companyDomains.slice(0, -1).join(", ") +
          ", or " +
          companyDomains[companyDomains.length - 1]
        : companyDomains[0];

    return (
      <span>
        {" "}
        Teammates with a matching {domainList} email can sign in to view the
        full transcript in Alpharun.
      </span>
    );
  };

  return (
    <>
      <ModalHeader onClose={onClose}>Share interview</ModalHeader>

      <div className="mt-4 flex w-full cursor-text space-x-2 rounded-md border border-solid border-border-strong bg-slate-50 px-2 py-2 text-xs hover:bg-slate-100">
        {shareSnippet.jsx}
      </div>

      <div className="mt-2 text-sm text-muted-foreground">
        <span>Paste this snippet into Slack, Teams, or emails to share.</span>
        {renderDomainLoginGuidance()}
      </div>

      <div className="mt-4 flex space-x-4">
        <Button
          variant={ButtonVariant.Primary}
          onClick={async () => {
            await copyRichText(shareSnippet.richText, shareSnippet.plainText);

            showToast({
              title: `Share text copied`,
              variant: ToastVariant.Success,
            });
          }}
        >
          Copy
        </Button>
        <Checkbox
          isChecked={shouldShareDetails}
          onChange={setShouldShareDetails}
          label={transcriptFragment ? "Include quote" : "Include summary"}
        />
      </div>
    </>
  );
};
// Generates share snippet in JSX, plaintext, and richtext formats.
function generateShareSnippet(
  primaryDisplayName: string,
  interview: Interview,
  transcriptFragment: TranscriptFragment | null,
  link: string,
  shouldShareDetails: boolean,
) {
  // If we're not sharing details, or we're trying to share the summary but don't have one,
  // and also no strengths and weaknesses, just return the link.
  if (
    !shouldShareDetails ||
    (shouldShareDetails &&
      !transcriptFragment &&
      !interview.insights?.summary &&
      (!interview.insights?.strengths || !interview.insights?.weaknesses))
  ) {
    return {
      plainText: link,
      richText: link,
      jsx: <Link href={link}>{link}</Link>,
    };
  }

  const linkElement = <Link href={link}>See the full interview</Link>;

  if (transcriptFragment) {
    const container = document.createElement("div");

    container.append("Quote from ");
    const strong = document.createElement("strong");
    strong.textContent = `${primaryDisplayName}'s`;
    container.appendChild(strong);
    container.append(" interview:");
    container.appendChild(document.createElement("br"));

    container.append("Q: ");
    container.append(transcriptFragment.question ?? "");
    container.appendChild(document.createElement("br"));

    container.append("A: ");
    container.append(transcriptFragment.text_transcript ?? "");
    container.appendChild(document.createElement("br"));
    container.appendChild(document.createElement("br"));

    const a = document.createElement("a");
    a.href = link;
    a.textContent = "See the full interview";
    container.appendChild(a);

    const richText = container.outerHTML;

    return {
      plainText: `Quote from ${primaryDisplayName}'s interview:\nQ: ${transcriptFragment.question}\nA: ${transcriptFragment.text_transcript}\n\nSee the full interview transcript and analysis:\n${link}`,
      richText,
      jsx: (
        <div>
          Quote from <strong>{primaryDisplayName}'s</strong> interview:
          <br />
          Q: {transcriptFragment.question}
          <br />
          A: {transcriptFragment.text_transcript}
          <br />
          <br />
          {linkElement}
        </div>
      ),
    };
  } else if (interview.insights?.summary) {
    // Summary should always exist here because of the earlier check.
    const summary = interview.insights.summary || "";
    const summaryLines = splitSummaryTextIntoLines(summary);

    // Build rich text using DOM APIs to avoid HTML injection and ensure proper escaping
    const container = document.createElement("div");

    container.append("Summary of interview with ");
    const strong = document.createElement("strong");
    strong.textContent = primaryDisplayName;
    container.appendChild(strong);
    container.append(":");
    container.appendChild(document.createElement("br"));

    summaryLines.forEach((line) => {
      container.append(line);
      container.appendChild(document.createElement("br"));
    });
    container.appendChild(document.createElement("br"));

    const a = document.createElement("a");
    a.href = link;
    a.textContent = "See the full interview";
    container.appendChild(a);

    const richText = container.outerHTML;

    return {
      plainText: `Summary of interview with ${primaryDisplayName}:\n${summaryLines.join(
        "\n",
      )}\n\nSee the full interview transcript and analysis:\n${link}`,
      richText,
      jsx: (
        <div>
          Summary of interview with <strong>{primaryDisplayName}</strong>:
          <br />
          {summary.split(". ").map((sentence, index) => (
            <div key={index}>
              - {sentence.trim()}
              {index !== summary.split(". ").length - 1 && (
                <>
                  {"."}
                  <br />
                </>
              )}
            </div>
          ))}
          <br />
          {linkElement}
        </div>
      ),
    };
  } else if (interview.insights?.strengths && interview.insights?.weaknesses) {
    // Add handling for strengths and weaknesses with summary line
    const strengths = interview.insights.strengths.join("\n");
    const weaknesses = interview.insights.weaknesses.join("\n");

    // Build rich text using DOM APIs to avoid HTML injection and ensure proper escaping
    const container = document.createElement("div");

    container.append("Summary of interview with ");
    const strongName = document.createElement("strong");
    strongName.textContent = primaryDisplayName;
    container.appendChild(strongName);
    container.append(":");
    container.appendChild(document.createElement("br"));

    // Strengths section
    container.append("Strengths:");
    container.appendChild(document.createElement("br"));
    interview.insights.strengths.forEach((s) => {
      container.append(`- ${s}`);
      container.appendChild(document.createElement("br"));
    });
    container.appendChild(document.createElement("br"));

    // Weaknesses section
    container.append("Weaknesses:");
    container.appendChild(document.createElement("br"));
    interview.insights.weaknesses.forEach((w) => {
      container.append(`- ${w}`);
      container.appendChild(document.createElement("br"));
    });
    container.appendChild(document.createElement("br"));

    const a = document.createElement("a");
    a.href = link;
    a.textContent = "See the full interview";
    container.appendChild(a);

    const richText = container.outerHTML;

    return {
      plainText: `Summary of interview with ${primaryDisplayName}:\nStrengths:\n${strengths}\n\nWeaknesses:\n${weaknesses}\n\nSee the full interview transcript and analysis:\n${link}`,
      richText,
      jsx: (
        <div>
          Summary of interview with <strong>{primaryDisplayName}</strong>:
          <br />
          Strengths:
          <br />
          {interview.insights.strengths.map((strength, index) => (
            <div key={index}>- {strength}</div>
          ))}
          <br />
          Weaknesses:
          <br />
          {interview.insights.weaknesses.map((weakness, index) => (
            <div key={index}>- {weakness}</div>
          ))}
          <br />
          {linkElement}
        </div>
      ),
    };
  } else {
    // Fallback to just the link if no details are available
    return {
      plainText: link,
      richText: link,
      jsx: <Link href={link}>{link}</Link>,
    };
  }
}
